<template>
  <div class="ad-creation">
    <step-progress
      :ad="ad"
      :currentStep="currentStep"
      :steps="[
          {number: 1, title: 'invite retailers', isComplete: step1Complete},
          {number: 2, title: 'choose recipients', isComplete: step2Complete},
          {number: 3, title: 'add image', isComplete: step3Complete},
          {number: 4, title: 'select timeframes', sComplete: step4Complete},
        ]"
      @validateStep="validateCurrentStep"/>

    <div :class="['error-message-bubble', errorMessage ? 'slide-down' : 'slide-up']"> {{errorMessage}} </div>

    <steps
        :ad="ad"
        :currentAdvertiserId="currentAdvertiser.id"
        :currentStep="currentStep"
        :isEdit="isEdit"
        :adSent="adSent"
        ref="steps"
        :imageLoading.sync="imageLoading"
        :steps="{
          1: {pageTitle: 'invite retailers to include your ad', action: 'next'},
          2: {pageTitle: step2Title, action: 'next'},
          3: {pageTitle: 'add an image', action: 'next'},
          4: {pageTitle: step4Title, action: 'complete'},
        }"
        @loadAd="loadAd"
        @setPageErrors="setPageErrors"
        @updateAd="updateAd"
        @validateStep="validateCurrentStep">
    </steps>
  </div>
</template>

<script>
  import AxiosMixin from '../mixins/AxiosMixin'
  import { adAPI } from '../../services/api/adAPI'
  import StepProgress from './StepProgress.vue'
  import Steps from './Steps.vue'

  export default {
    props: {
      currentAdvertiser: Object,
    },

    name: 'AdEdit',

    mixins: [AxiosMixin],

    components: {
      StepProgress,
      Steps,
    },

    data() {
      return {
        ad: {},
        currentStep: 1,
        errorMessage: null,
        errors: {},
        extraRouteData: null,
        id: null,
        step1Complete: null,
        step2Complete: null,
        step3Complete: null,
        step4Complete: null,
        imageLoading: null
      }
    },

    computed: {
      isEdit: function() {
        return this.ad?.status === 'active'
      },

      adSent: function() {
        return this.ad?.ad_sent
      },

      step2Title: function() {
        const actionWord = this.isEdit ? 'increase' : 'select'
        return `${actionWord} the total number of recipients`
      },

      step4Title: function() {
        const createLabel = 'select the available date range for retailers and the tracking timeframe after reception for customers',
            editLabel = 'select an available end date for retailers'
        return this.isEdit ? editLabel : createLabel
      }
    },

    methods: {
      changeStep(action) {
        const currentStepComplete = `step${this.currentStep}Complete`

        if (action === 'complete' || action === 'next') {
          this[currentStepComplete] = true
          action === 'complete' ? this.$refs?.steps?.setCostModal() : this.currentStep += 1
        } else if ([1,2,3,4].includes(action)) {
          this.currentStep = action
        }
      },

      async loadAd() {
        try {
          const ad = this.id ? await adAPI.getAd(this.currentAdvertiser.id, this.id) :
                               await adAPI.createAd(this.currentAdvertiser.id, this.extraRouteData)
          this.loadAdSuccessHandler(ad)
        } catch(e) {
          this.onError(e)
        }
      },

      loadAdSuccessHandler(data) {
        if (data) {
          if (this.id) {
            this.ad = data
          } else {
            this.id = data.id
            // Assign value to this id so when we hot-reload, it won't create a new ad
            this.$route.params.id = this.id
            this.$router.replace({path: `/ad/${this.id}`})
          }
          if (this.extraRouteData?.marketplace_sponsor_request_id) {
            this.ad = data
            this.changeStep('next')
          }
          this.setPageErrors('adApi')
        } else {
          this.setPageErrors('adApi', 'there was an error loading ad, please refresh or contact support')
        }
      },

      onError(error) {
        this.setPageErrors('adApi', `there was an issue: ${error}`)
        this.$refs.steps?.imageLoadingErrorHandler()
      },

      setPageErrors(field, errorMessage) {
        if (errorMessage && !this.errors[field]) {
          this.$set(this.errors, field, errorMessage)
        } else if (!errorMessage && this.errors[field]) {
          this.$delete(this.errors, field)
        } else if (this.errors[field] && this.errors[field] != errorMessage) {
          this.$delete(this.errors, field)
          this.$set(this.errors, field, errorMessage)
        }

        this.updateErrorMessage()
      },

      async updateAd(dataToUpdate, additionalParams = {}, isForCompletion) {
        if (!this.id) { return; }
        this.imageLoading = true
        dataToUpdate.id = this.id

        try {
          const data = await adAPI.updateAd(this.currentAdvertiser.id, dataToUpdate, additionalParams);
          // Re-fetch requests after fulfilled ad w/ request is complete
          if (isForCompletion && !!data.marketplace_sponsor_request_id) {
            await this.$store.dispatch('FETCH_MERCHANT_REQUESTS', { advertiserID: this.currentAdvertiser.id })
          }
          this.updateAdSuccessHandler(data, {isForCompletion});
        } catch (error) {
          this.onError(error);
        }
      },

      updateAdSuccessHandler(data) {
        this.setPageErrors('adApi')
        this.imageLoading = false

        if (data) {
          this.ad = data

          if (!data?.marketplace_sponsor_request_id && (!data.retailers || data.retailers?.length === 0)) {
            this.setPageErrors('adApi', 'must select at least one retailer or sponsor request');
          }
        } else {
          this.setPageErrors('adApi', 'there was an error updating ad, please refresh or contact support')
        }
      },

      updateErrorMessage() {
        const errors = Object.values(this.errors)
        this.errorMessage = errors.join(', ')
      },

      validateCurrentStep(action) {
        const stepIsValid = Object.keys(this.errors).length < 1
        stepIsValid ? this.changeStep(action) : null
      },
    },

    created() {
      this.id = this.$route?.params?.id
      this.extraRouteData = this.$route?.params?.extraData
      performance.navigation && performance.navigation.type == 2 ? window.location.href= '/' : this.loadAd()
    }
  }
</script>

<style lang="sass">
@import '../../assets/sass/colors'

.ad-creation
  display: flex
  flex-direction: column
  position: absolute
  left: 0
  right: 0
  top: 0
  bottom: 0
  .error-message-bubble
    background: $action-alert
    color: $title-color
    font-size: 1.15rem
    margin: 0
    padding: .5em
    text-align: center
    transition: all 0.5s ease
    &.slide-up
      opacity: 0
    &.slide-down
      opacity: 1
</style>
